import { useMemo } from 'react';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { includes } from 'ramda';
import { useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import InfoCircleTooltip from 'components/atoms/GefMui/InfoCircleTooltip';
import { codelists } from 'constants/codelists';
import { USER_ROLES } from 'constants/general';
import { notificationSetIds } from 'features/views/settings/constants';
import { getSwitchDescription } from 'features/views/settings/utils';
import { getRoles } from 'features/views/user/selectors';
import { getContextUser, getContextUserHasRole } from 'features/views/userContext/selectors';
import { getCodelistByName } from 'utils';

import SettingsSwitch from './SettingsSwitch';
import SettingsSwitchWithModalLink from './SettingsSwitchWithModalLink';

const {
	EOBCHODNIK_PUSH_NOTIFS_TASK_CREATION,
	EOBCHODNIK_PUSH_NOTIFS_LEAD_CREATION,
	EOBCHODNIK_PUSH_NOTIFS_CONTACT_ASSIGN,
	EOBCHODNIK_PUSH_NOTIFS_CAMPAIGN_NEAR_END,
} = notificationSetIds;

export const EObchodnikNotifications = () => {
	const notificationsSetCodelist = useSelector(getCodelistByName(codelists.notificationsSet));
	const notificationsSetContactsCodelist = useSelector(getCodelistByName(codelists.notificationsSetContacts));

	const getSwitchShortDescription = getSwitchDescription(notificationsSetCodelist);
	const getContactsSwitchShortDescription = getSwitchDescription(notificationsSetContactsCodelist);
	const mainUserRoles = useSelector(getRoles);
	const isMainUserSellerOrPPz = useMemo(
		() => includes(USER_ROLES.SELLER, mainUserRoles) || includes(USER_ROLES.PPZ, mainUserRoles),
		[mainUserRoles],
	);

	const contextUser = useSelector(getContextUser);
	const isContextUserSeller = useSelector((state) => getContextUserHasRole(state, USER_ROLES.SELLER));
	const isContextUserPPZ = useSelector((state) => getContextUserHasRole(state, USER_ROLES.PPZ));

	const userHasSellerOrPPZRole = useMemo(() => {
		if (contextUser) {
			return isContextUserSeller || isContextUserPPZ;
		} else {
			return isMainUserSellerOrPPz;
		}
	}, [contextUser, isContextUserPPZ, isContextUserSeller, isMainUserSellerOrPPz]);

	const settingsHeader = (
		<Stack
			spacing={1}
			direction="row"
			sx={{
				alignItems: 'center',
			}}
		>
			<Typography variant="h5" color="text.primary">
				{getLabelMessage('settings:eObchodnikNotifications')}
			</Typography>
			<InfoCircleTooltip text={getLabelMessage('settings:eObchodnikNotificationsHeaderTooltip')} />
		</Stack>
	);

	return (
		<Grid size={12}>
			<Grid container>
				<Grid size={12}>{settingsHeader}</Grid>
				<Grid size={12}>
					<SettingsSwitchWithModalLink
						description={getSwitchShortDescription(EOBCHODNIK_PUSH_NOTIFS_TASK_CREATION)}
						notificationSetId={EOBCHODNIK_PUSH_NOTIFS_TASK_CREATION}
					/>
					<SettingsSwitchWithModalLink
						description={getSwitchShortDescription(EOBCHODNIK_PUSH_NOTIFS_LEAD_CREATION)}
						notificationSetId={EOBCHODNIK_PUSH_NOTIFS_LEAD_CREATION}
					/>
					{userHasSellerOrPPZRole && (
						<SettingsSwitch
							description={getContactsSwitchShortDescription(EOBCHODNIK_PUSH_NOTIFS_CONTACT_ASSIGN)}
							notificationSetId={EOBCHODNIK_PUSH_NOTIFS_CONTACT_ASSIGN}
							doNotUseCodelistPermissionForSwitch
						/>
					)}
					{userHasSellerOrPPZRole && (
						<SettingsSwitch
							description={getContactsSwitchShortDescription(EOBCHODNIK_PUSH_NOTIFS_CAMPAIGN_NEAR_END)}
							notificationSetId={EOBCHODNIK_PUSH_NOTIFS_CAMPAIGN_NEAR_END}
							doNotUseCodelistPermissionForSwitch
						/>
					)}
				</Grid>
			</Grid>
		</Grid>
	);
};

export default EObchodnikNotifications;
